import React, { useMemo } from "react";
import { SbCheckbox, SbTable, SbTooltip, TooltipTriggerStyled } from "uiCore";
import Permission from "Components/Permission";
import useEntitlementsStore from "Store/useEntitlementsStore";
import { DownloadOutlined } from "@ant-design/icons";
import { getAgentBuildDownloadUrl } from "Api/apiService";
import useAuthStore from "Store/useAuthStore";
import { buildFieldConfigMap } from "Utils/formatting";

import {
  TableShell,

} from "./SbPACGroupAgentsV2.styles";

// Tooltip content strings (PRD §5.11)
const TOOLTIP_SELECT_AGENT_CHECKBOX =
  "Include this AI agent in your current version selection. The stats panel on the right updates in real time as you select.";
const TOOLTIP_SELECT_ALL_CHECKBOX =
  "Select or deselect all AI agents in the current view.";
const TOOLTIP_AGENT_ROW =
  "Click to open the Agent Details page and see which roles this agent affects, the tasks it automates, and its estimated value.";
const TOOLTIP_ROLE_ROW =
  "Click to open the Role Impact Analysis for this role — showing task-by-task AI impact and workflow changes.";

const compareNumber = (key) => (a, b) =>
  Number(a?.[key] ?? 0) - Number(b?.[key] ?? 0);

const PACGroupAgentsTableV2 = ({
  viewByToggle,
  PAICAgents = [],
  PAICRoles = [],
  selectedAgentIds,
  handleSelectAll,
  toggleAgentSelection,
  handleRowClick,
  handleRoleRowClick,
  formatPercentage,
  formatCurrencyShort,
}) => {
  const orgMetaData = useAuthStore((s) => s.orgMetaData);
  const fieldMap = useMemo(() => buildFieldConfigMap(orgMetaData, 'req_no'), [orgMetaData]);

  const entitlements = useEntitlementsStore((s) => s.entitlements);
  
  const hasCompEntitlement = entitlements
    ?.find((e) => e.name === "people_plus_ai")
    ?.entitlements?.includes("view_comp");

  // Hours cells render inside <Permission view_hours>; keep their filters (which list
  // the raw values) hidden from users who can't see the cells.
  const paiActions =
    entitlements?.find((e) => e.name === "people_plus_ai")?.entitlements || [];
  const hasHoursEntitlement =
    paiActions.includes("view_hours") || paiActions.includes("*");

  const allAgentsSelected =
    PAICAgents.length > 0 && selectedAgentIds.size === PAICAgents.length;

  const downloadBtnStyle = {
    background: "none", border: "none", cursor: "pointer",
    color: "#137333", padding: "4px 6px", borderRadius: 4,
    display: "inline-flex", alignItems: "center",
  };

  const someAgentsSelected =
    selectedAgentIds.size > 0 && selectedAgentIds.size < PAICAgents.length;

  const agentsColumnsBase = [
    {
      title: (
        <SbTooltip content={TOOLTIP_SELECT_ALL_CHECKBOX}>
          <TooltipTriggerStyled>
            <SbCheckbox
              checked={allAgentsSelected}
              indeterminate={someAgentsSelected}
              onChange={(e) => handleSelectAll(e?.target?.checked)}
            />
          </TooltipTriggerStyled>
        </SbTooltip>
      ),
      dataIndex: "select",
      key: "select",
      width: 48,
      align: "center",
      filterable: false,
      render: (_, record) => (
        <div className="paic-row-action" onClick={(e) => e.stopPropagation()}>
          <SbTooltip content={TOOLTIP_SELECT_AGENT_CHECKBOX}>
            <TooltipTriggerStyled>
              <SbCheckbox
                checked={selectedAgentIds.has(record?.sb_agent_unique_id)}
                onChange={() => toggleAgentSelection(record?.sb_agent_unique_id)}
              />
            </TooltipTriggerStyled>
          </SbTooltip>
        </div>
      ),
    },
    {
      title: "Name",
      dataIndex: "ai_agent_name",
      key: "ai_agent_name",
      align: "left",
      render: (text) => (
        <SbTooltip content={TOOLTIP_AGENT_ROW}>
          <span>{text || "-"}</span>
        </SbTooltip>
      ),
    },
    {
  title: "Description",
  dataIndex: "ai_agent_description",
  key: "ai_agent_description",
  align: "left",
  ellipsis: { showTitle: false },
  render: (text, record) => {
    const description = text || "-";
    const agentCode = record?.sb_agent_code || "-";
    const microTasks = record?.no_of_microtasks_impacted ?? "-";

    return (
      <SbTooltip
        placement="top"
        overlayClassName="paic-description-tooltip"
        title={
          <div>
            <div>
              <strong>Description:</strong> {description}
            </div>
            <div >
              <strong>Agent Code:</strong> {agentCode}
            </div>
            <div >
              <strong>Micro-Tasks:</strong> {microTasks}
            </div>
          </div>
        }
      >
        <span className="paic-description-cell">{description}</span>
      </SbTooltip>
    );
  },
},
    {
      title: "AI Potential",
      dataIndex: "ai_percent_total_base_c",
      key: "ai_percent_total_base_c",
      align: "right",
      sorter: compareNumber("ai_percent_total_base_c"),
      defaultSortOrder: "descend",
      sortDirections: ["descend", "ascend"],
      render: (value) => formatPercentage(value),
    },
    {
      title: "Roles Supported",
      dataIndex: "no_of_roles_impacted",
      key: "no_of_roles_impacted",
      align: "right",
      sorter: compareNumber("no_of_roles_impacted"),
      sortDirections: ["ascend", "descend"],
      render: (value) => value ?? 0,
    },
    {
      title: "Positions Supported",
      dataIndex: "no_of_positions_impacted",
      key: "no_of_positions_impacted",
      align: "right",
      sorter: compareNumber("no_of_positions_impacted"),
      sortDirections: ["ascend", "descend"],
      render: (value) => value ?? 0,
    },
  ];

  // Financial columns - show values for entitled users, ranks for non-entitled users
  const financialColumns = [
    {
      title: hasCompEntitlement ? "Potential Value" : "Rank - Potential Value",
      dataIndex: hasCompEntitlement ? "total_value_to_business_c" : "potential_value_rank",
      key: hasCompEntitlement ? "total_value_to_business_c" : "potential_value_rank",
      align: "right",
      sorter: hasCompEntitlement
        ? compareNumber("total_value_to_business_c")
        : compareNumber("potential_value_rank"),
      sortDirections: ["ascend", "descend"],
      render: (value, record) =>
        hasCompEntitlement 
          ? formatCurrencyShort(value)
          : `${record.potential_value_rank || "-"}`,
    },
    {
      title: hasCompEntitlement ? "Dev Cost" : "Rank - Dev Cost",
      dataIndex: hasCompEntitlement ? "estimated_development_cost" : "development_cost_rank",
      key: hasCompEntitlement ? "estimated_development_cost" : "development_cost_rank",
      align: "right",
      sorter: hasCompEntitlement
        ? compareNumber("estimated_development_cost")
        : compareNumber("development_cost_rank"),
      sortDirections: ["ascend", "descend"],
      render: (value, record) =>
        hasCompEntitlement
          ? formatCurrencyShort(value)
          : `${record.development_cost_rank || "-"}`,
    },
    {
      title: hasCompEntitlement ? "ROI" : "Rank - ROI",
      dataIndex: hasCompEntitlement ? "ROI_C" : "roi_rank",
      key: hasCompEntitlement ? "ROI_C" : "roi_rank",
      align: "right",
      sorter: hasCompEntitlement
        ? compareNumber("ROI_C")
        : compareNumber("roi_rank"),
      sortDirections: ["ascend", "descend"],
      render: (value, record) =>
        hasCompEntitlement
          ? `${Number(value) || 0}`
          : `${record.roi_rank || "-"}`,
    },
  ];

  const handleDownloadAgent = async (buildId, e) => {
    e.stopPropagation();
    try {
      const res = await getAgentBuildDownloadUrl(buildId);
      const a = document.createElement("a");
      a.href = res.download_url;
      a.download = "";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error("Agent download failed:", err);
    }
  };

  const agentColumn = {
    title: "Agent",
    dataIndex: "build_status",
    key: "build_status",
    align: "center",
    width: 90,
    filterable: false,
    render: (buildStatus, record) => {
      const buildId = record?.build_id;
      if (buildStatus === "DONE" && buildId) {
        return (
          <SbTooltip content="Download Agent ZIP" placement="top">
            <button
              className="paic-row-action"
              title="Download Agent ZIP"
              onClick={(e) => handleDownloadAgent(buildId, e)}
              style={downloadBtnStyle}
            >
              <DownloadOutlined style={{ fontSize: 14 }} />
            </button>
          </SbTooltip>
        );
      }
      return null;
    },
  };

  const hasAnyBuild = PAICAgents.some((a) => a.build_status === "DONE");
  const agentsColumns = [...agentsColumnsBase, ...financialColumns, ...(hasAnyBuild ? [agentColumn] : [])];

  const rolesColumnsBase = [
    {
      title: fieldMap['job_code']?.label || "Role Id",
      dataIndex: "sb_requisition_number",
      key: "sb_requisition_number",
      align: "left",
      ellipsis: true,
      render: (text) => text || "-",
    },
    {
      title: "Role Name",
      dataIndex: "job_title",
      key: "job_title",
      align: "left",
      render: (text) => (
        <SbTooltip content={TOOLTIP_ROLE_ROW}>
          <span>{text || "-"}</span>
        </SbTooltip>
      ),
    },
    {
      title: "Positions",
      dataIndex: "total_positions",
      key: "total_positions",
      align: "right",
      sorter: compareNumber("total_positions"),
      sortDirections: ["ascend", "descend"],
      render: (value) => value ?? 0,
    },
    {
      title: "Automation Potential",
      dataIndex: "total_role_microtask_ai_percentage_c",
      key: "total_role_microtask_ai_percentage_c",
      align: "right",
      sorter: compareNumber("total_role_microtask_ai_percentage_c"),
      defaultSortOrder: "descend",
      sortDirections: ["descend", "ascend"],
      render: (value) => formatPercentage(value),
    },
    {
      title: "Hours / Role",
      dataIndex: "total_role_hours",
      key: "total_role_hours",
      align: "right",
      sorter: compareNumber("total_role_hours"),
      sortDirections: ["ascend", "descend"],
      filterable: hasHoursEntitlement,
      filterLabel: (value) => value ?? 0,
      render: (value) => (
        <Permission resource="people_plus_ai" actions={["view_hours"]}>
          {value ?? 0}
        </Permission>
      ),
    },
    {
      title: "Hours Saved",
      dataIndex: "potential_impact_hours_c",
      key: "potential_impact_hours_c",
      align: "right",
      sorter: compareNumber("potential_impact_hours_c"),
      sortDirections: ["ascend", "descend"],
      filterable: hasHoursEntitlement,
      filterLabel: (value) => Math.round(value ?? 0),
      render: (value) => (
        <Permission resource="people_plus_ai" actions={["view_hours"]}>
          {Math.round(value ?? 0)}
        </Permission>
      ),
    },
  ];

  // Financial columns for roles - only shown to users with view_comp entitlement
  const rolesFinancialColumns = hasCompEntitlement
    ? [
        {
          title: "Avg Comp",
          dataIndex: "total_comp",
          key: "total_comp",
          align: "right",
          sorter: compareNumber("total_comp"),
          sortDirections: ["ascend", "descend"],
          filterLabel: (value) => formatCurrencyShort(value),
          render: (value) => (
            <Permission resource="people_plus_ai" actions={["view_comp"]}>
              {formatCurrencyShort(value)}
            </Permission>
          ),
        },
        {
          title: "Potential Value",
          dataIndex: "total_role_saving_c",
          key: "total_role_saving_c",
          align: "right",
          sorter: compareNumber("total_role_saving_c"),
          sortDirections: ["ascend", "descend"],
          filterLabel: (value) => formatCurrencyShort(value),
          render: (value) => (
            <Permission resource="people_plus_ai" actions={["view_comp"]}>
              {formatCurrencyShort(value)}
            </Permission>
          ),
        },
      ]
    : [];

  const rolesColumns = [...rolesColumnsBase, ...rolesFinancialColumns];

  return (
    <TableShell>
      
        {!viewByToggle ? (
          <SbTable
            columns={agentsColumns}
            dataSource={PAICAgents}
            pagination={false}
            rowKey={(record, index) =>
              record?.sb_agent_unique_id ?? record?.agent_id ?? index
            }
            tableLayout="fixed"
            sticky
            onRow={(record) => ({
              className: "paic-clickable-row",
              title: "Click to open Agent Details",
              onClick: () => handleRowClick(record?.sb_agent_unique_id),
            })}
            
          />
        ) : (
          <SbTable
            columns={rolesColumns}
            dataSource={PAICRoles}
            pagination={false}
            rowKey={(record, index) =>
              record?.sb_unique_id ?? record?.job_id ?? index
            }
            tableLayout="fixed"
            sticky
            onRow={(record) => ({
              className: "paic-clickable-row",
              title: "Click to open Role Impact Details",
              onClick: () => handleRoleRowClick(record),
            })}
          />
        )}
      
    </TableShell>
  );
};

export default PACGroupAgentsTableV2;